import {
  SxProps,
  Theme,
  Autocomplete,
  AutocompleteInputChangeReason,
  AutocompleteRenderInputParams,
  createFilterOptions,
  FilterOptionsState,
  AutocompleteChangeReason,
} from "@mui/material";
import React, { useCallback, useMemo, useRef, useState } from "react";
import TextField from "../atoms/TextField";
import { Typography } from "../atoms/Typography";

const SearchStyle = {
  "& .MuiFormControl-root": {
    border: "0.5px solid #c4c4c4",
    borderRadius: "4px",
    height: "40px",
    "& .MuiFormLabel-root": {
      marginTop: "-11px",
      backgroundColor: "#fff",
      paddingLeft: "10px",
    },
    "& .MuiInputBase-root": {
      "& .MuiAutocomplete-endAdornment": {
        top: "50%",
        transform: "translateY(calc(-50% - 3px))",
        right: "8px",
      },
      marginTop: "6px",
      "::before": { borderBottom: "none" },
    },
  },
} as const;

type ExOption<T> = T & {
  ITEM_LABEL: string;
  SELECT_ALL?: true;
};

const OPTION_SELECT_ALL = {
  ITEM_LABEL: "すべて選択",
  SELECT_ALL: true,
} as const;

const LABEL_MAPPER = <T,>({ ITEM_LABEL }: ExOption<T>) => ITEM_LABEL;
const OPTIONS_EQUAL = <T,>(a: ExOption<T>[], b: ExOption<T>[]) =>
  a
    .map(({ ITEM_LABEL }) => ITEM_LABEL)
    .sort()
    .join() ===
  b
    .map(({ ITEM_LABEL }) => ITEM_LABEL)
    .sort()
    .join();

type AutoCompleteSearchProps<T> = {
  options: T[];
  value: T[];
  label?: string;
  sx?: SxProps<Theme>;
  error?: boolean;
  forSearch?: boolean;
  key?: string;
  uniqueKey?: keyof T;
  limit?: number;
  getOptionLabel: (option: T) => string;
  onChange: (value: T[]) => void;
};

export const AutoCompleteMultiple = <T,>({
  options,
  value,
  label,
  sx,
  error,
  forSearch,
  key,
  uniqueKey,
  limit,
  getOptionLabel,
  onChange,
}: AutoCompleteSearchProps<T>) => {
  const requireLimit = useMemo(
    () => limit != null && limit < options.length,
    [limit, options.length]
  );

  const exOptions = useMemo<ExOption<T>[]>(
    () =>
      options.map((op) => ({
        ...op,
        ITEM_LABEL: getOptionLabel(op),
      })),
    [getOptionLabel, options]
  );

  const selectedOptions = useMemo<ExOption<T>[]>(
    () =>
      value.map((op) => ({
        ...op,
        ITEM_LABEL: getOptionLabel(op),
      })) ?? [],
    [getOptionLabel, value]
  );

  const isOptionEqualToValue = useCallback(
    (o: ExOption<T>, v: ExOption<T>) => {
      return o[uniqueKey ?? "ITEM_LABEL"] === v[uniqueKey ?? "ITEM_LABEL"];
    },
    [uniqueKey]
  );

  const [inputValue, setInputValue] = useState("");
  const handleInputChange = useCallback(
    (
      _: React.SyntheticEvent<Element, Event>,
      value: string,
      reason: AutocompleteInputChangeReason
    ) => {
      ["clear", "input"].includes(reason) && setInputValue(value);
    },
    []
  );

  const filter = useMemo(() => createFilterOptions<ExOption<T>>(), []);
  const [filteredOptions, setFilteredOptions] = useState<ExOption<T>[]>([]);

  const handleChange = useCallback(
    (
      _: React.SyntheticEvent<Element, Event>,
      selected: ExOption<T>[],
      reason: AutocompleteChangeReason
    ) => {
      if (reason === "clear") {
        onChange([]);
        return;
      }

      if (reason !== "selectOption" && reason !== "removeOption") {
        return;
      }

      if (selected.some(({ SELECT_ALL }) => SELECT_ALL)) {
        const targetOptions =
          inputValue.length === 0 ? exOptions : filteredOptions;
        onChange(
          selectedOptions.length < targetOptions.length ? targetOptions : []
        );
        return;
      }

      if (!requireLimit || (limit != null && limit >= selected.length)) {
        onChange(selected.filter(({ SELECT_ALL }) => !SELECT_ALL));
      }
    },
    [
      limit,
      requireLimit,
      onChange,
      inputValue.length,
      exOptions,
      filteredOptions,
      selectedOptions.length,
    ]
  );

  const timerRef = useRef(setTimeout(() => void 0));
  const filterRef = useRef<ExOption<T>[]>([]);

  const filterOptions = useCallback(
    (options: ExOption<T>[], state: FilterOptionsState<ExOption<T>>) => {
      clearTimeout(timerRef.current);
      const filtered = filter(options, state);
      const limitted = filtered.filter((_, i) => limit == null || i < limit);

      if (
        state.inputValue.length !== 0 &&
        !OPTIONS_EQUAL(limitted, filterRef.current)
      ) {
        timerRef.current = setTimeout(() => {
          onChange(limitted);
          setFilteredOptions((filterRef.current = limitted));
        }, 100);
      }
      return requireLimit || filtered.length === 0
        ? filtered
        : [OPTION_SELECT_ALL as ExOption<T>, ...filtered];
    },
    [filter, limit, onChange, requireLimit]
  );

  const renderTags = useCallback(
    (values: ExOption<T>[]) => {
      const count = values.filter(({ SELECT_ALL }) => !SELECT_ALL).length;
      const full = requireLimit && limit != null && limit <= count;
      const color = full ? "#1976d2" : "rgba(0, 0, 0, 0.6)";
      const message = `${count} 件選択中${full ? "(最大)" : ""}`;

      return (
        <Typography sx={{ mr: 1, color, fontSize: "16px" }}>
          {message}
        </Typography>
      );
    },
    [limit, requireLimit]
  );

  const renderInput = useCallback(
    (params: AutocompleteRenderInputParams) => (
      <TextField
        {...params}
        sx={{ paddingLeft: 1, ...sx }}
        minWidth="100%"
        label={label}
        error={error}
      />
    ),
    [error, label, sx]
  );

  const autocompleteSX = useMemo(
    () => (forSearch ? SearchStyle : undefined),
    [forSearch]
  );

  const component = useMemo(
    () => (
      <Autocomplete
        multiple
        disableCloseOnSelect
        key={key}
        options={exOptions}
        value={selectedOptions}
        inputValue={inputValue}
        sx={autocompleteSX}
        getOptionLabel={LABEL_MAPPER}
        isOptionEqualToValue={isOptionEqualToValue}
        onChange={handleChange}
        onInputChange={handleInputChange}
        onBlur={() => setInputValue("")}
        renderTags={renderTags}
        renderInput={renderInput}
        filterOptions={filterOptions}
        renderGroup={(params) => params as unknown as React.ReactNode}
      />
    ),
    [
      autocompleteSX,
      exOptions,
      filterOptions,
      handleChange,
      handleInputChange,
      inputValue,
      isOptionEqualToValue,
      key,
      renderInput,
      renderTags,
      selectedOptions,
    ]
  );

  return <>{component}</>;
};
